import { type ImageDescription, generateArticleTitle, generateDescription } from "../api";
import type { Article, Photo, Section, VersionedSection } from "../types";
import { isAbortError } from "./errors";

const DEFAULT_BATCH_SIZE = 3;

export interface UpdateArticleDependencies {
  describePhoto: typeof generateDescription;
  generateTitle: typeof generateArticleTitle;
  batchSize: number;
  now: () => Date;
  signal?: AbortSignal;
  photoIds?: readonly string[];
}

const defaultDependencies: UpdateArticleDependencies = {
  describePhoto: generateDescription,
  generateTitle: generateArticleTitle,
  batchSize: DEFAULT_BATCH_SIZE,
  now: () => new Date(),
};

interface ProcessedPhoto {
  index: number;
  section: Section;
  versionHistory?: VersionedSection;
}

export async function updateArticle(
  article: Article,
  setArticle: (article: Partial<Article>) => void,
  dependencyOverrides: Partial<UpdateArticleDependencies> = {},
) {
  const dependencies = { ...defaultDependencies, ...dependencyOverrides };
  if (!Number.isInteger(dependencies.batchSize) || dependencies.batchSize < 1) {
    throw new Error("Batch size must be a positive integer");
  }

  const processedSections = article.sections.map((section) => ({ ...section }));
  let versionHistory = { ...article.versionHistory };
  const selectedPhotoIds = dependencies.photoIds ? new Set(dependencies.photoIds) : null;
  const pendingIndices = processedSections.flatMap((section, index) =>
    section.description || (selectedPhotoIds && !selectedPhotoIds.has(section.id)) ? [] : [index],
  );
  let processedCount = 0;

  setArticle({
    isLoading: true,
    error: undefined,
    progress: { current: processedCount, total: pendingIndices.length },
  });

  try {
    for (let offset = 0; offset < pendingIndices.length; offset += dependencies.batchSize) {
      dependencies.signal?.throwIfAborted();
      const batchIndices = pendingIndices.slice(offset, offset + dependencies.batchSize);
      for (const index of batchIndices) {
        processedSections[index] = { ...processedSections[index], isProcessing: true };
      }
      setArticle({ sections: [...processedSections] });

      const results = await Promise.all(
        batchIndices.map((index) => processPhoto(index, processedSections, dependencies)),
      );

      for (const result of results) {
        processedSections[result.index] = result.section;
        if (result.versionHistory) {
          versionHistory = {
            ...versionHistory,
            [result.section.id]: result.versionHistory,
          };
          processedCount++;
        }
      }

      setArticle({
        sections: [...processedSections],
        versionHistory,
        progress: { current: processedCount, total: pendingIndices.length },
      });
    }

    let articleTitle = article.articleTitle;
    if (processedSections.length >= 2) {
      try {
        articleTitle = await dependencies.generateTitle(
          processedSections.map(photoToImageDescription),
          dependencies.signal,
        );
      } catch (error) {
        if (isAbortError(error)) throw error;
        console.error("Failed to generate article title:", error);
      }
    }

    const dates = processedSections
      .map((section) => section.date)
      .filter((date): date is string => Boolean(date))
      .map((date) => new Date(date))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    const failedCount = processedSections.filter((section) => section.error).length;

    setArticle({
      sections: processedSections,
      versionHistory,
      articleTitle,
      isLoading: false,
      error:
        failedCount > 0
          ? `${failedCount} ${failedCount === 1 ? "photo" : "photos"} could not be processed.`
          : undefined,
      dateRange:
        dates.length > 0
          ? {
              start: dates[0].toISOString().split("T")[0],
              end: dates[dates.length - 1].toISOString().split("T")[0],
            }
          : undefined,
      progress: undefined,
    });
  } catch (error) {
    setArticle({
      sections: processedSections.map((section) => ({ ...section, isProcessing: false })),
      isLoading: false,
      error: isAbortError(error) ? undefined : "Article generation failed. Please try again.",
      progress: undefined,
    });
    throw error;
  }

  return { canceled: false };
}

async function processPhoto(
  index: number,
  processedSections: Section[],
  dependencies: UpdateArticleDependencies,
): Promise<ProcessedPhoto> {
  const photo = processedSections[index];
  const previousDescriptions = processedSections
    .slice(0, index)
    .filter((section) => section.description)
    .map(photoToImageDescription);

  try {
    const description = await dependencies.describePhoto(
      photo.file,
      previousDescriptions,
      photo.userNotes,
      dependencies.signal,
    );
    return {
      index,
      section: {
        ...photo,
        ...description,
        error: undefined,
        isProcessing: false,
      },
      versionHistory: {
        versions: [
          {
            title: description.title,
            description: description.description,
            timestamp: dependencies.now().toISOString(),
          },
        ],
        currentVersion: 0,
      },
    };
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error(`Failed to process photo ${index}:`, error);
    return {
      index,
      section: {
        ...photo,
        isProcessing: false,
        error: error instanceof Error ? error.message : "Failed to process photo",
      },
    };
  }
}

function photoToImageDescription(photo: Photo): ImageDescription {
  return {
    title: photo.title || "",
    caption: photo.caption || "",
    description: photo.description || "",
    sign_translation: photo.sign_translation,
    location_name: photo.location_name,
    date: photo.date,
    latitude: photo.latitude,
    longitude: photo.longitude,
    camera: photo.camera,
    lens: photo.lens,
  };
}
